// Rules-based matching engine for the site assistant.
//
// Pure functions, no React and no DOM — so it can be unit-tested in Node and
// reused anywhere. It scores a visitor's message against the knowledge base
// and returns ranked matches with a confidence score. Everything here runs
// locally: no network, no API key, no per-message cost.

const STOPWORDS = new Set([
  "a", "an", "the", "i", "im", "my", "me", "we", "our", "you", "your", "is",
  "are", "am", "be", "was", "were", "do", "does", "did", "can", "could",
  "would", "should", "will", "to", "for", "of", "in", "on", "at", "and",
  "or", "it", "this", "that", "with", "have", "has", "need", "want", "get",
  "please", "any", "some", "about", "from", "if", "so", "just", "all",
  // Question words and fillers. A phrase built only from these — "what do you
  // do" — carries no meaning token by token, so it is matched whole or not at
  // all. That is what keeps "what areas do you cover" off the services list.
  "what", "how", "where", "when", "who", "why", "which", "much", "many",
  "there", "here", "into", "than", "then", "been", "being", "doing", "done",
  "like", "see", "tell", "know", "going", "go", "but", "not", "us", "out",
  "up", "one", "also", "really", "thing", "things",
]);

// Trade and Canadian-construction vocabulary the stemmer can't reach.
const SYNONYMS = {
  reno: "renovation", renos: "renovation", renovate: "renovation",
  renovating: "renovation", remodel: "renovation", remodeling: "renovation",
  remodelling: "renovation", refurb: "renovation", renovations: "renovation",
  bathrooms: "bathroom", washroom: "bathroom", ensuite: "bathroom",
  kitchens: "kitchen", basements: "basement", cellar: "basement",
  suites: "suite", electric: "electrical", electrician: "electrical",
  wiring: "electrical", plumber: "plumbing", plumb: "plumbing",
  roofs: "roof", roofer: "roof", roofing: "roof", shingles: "shingle",
  reroof: "roof", sheetrock: "drywall", furnace: "hvac", ac: "hvac",
  "air-conditioning": "hvac", heating: "hvac", cooling: "hvac",
  floors: "flooring", hardwoods: "hardwood", tiles: "tile", tiling: "tile",
  painter: "painting", paint: "painting", painters: "painting",
  decks: "deck", pergolas: "pergola", fences: "fence",
  concreting: "concrete", cement: "concrete", foundations: "foundation",
  demo: "demolition", demolish: "demolition", teardown: "demolition",
  additions: "addition", extension: "addition", extend: "addition",
  quotes: "quote", estimate: "quote", estimates: "quote", quoting: "quote",
  pricing: "price", prices: "price", cost: "price", costs: "price",
  costing: "price", charge: "price", charges: "price", rate: "price",
  rates: "price", expensive: "price", cheap: "price", afford: "price",
  budget: "price",
  phone: "contact", call: "contact", email: "contact", reach: "contact",
  licence: "licensed", license: "licensed", licencing: "licensed",
  insurance: "insured", warranties: "warranty", guaranteed: "guarantee",
  timeline: "long", duration: "long", services: "service",
  house: "home", houses: "home", homes: "home", property: "home",
  websites: "website", "web-design": "website", webdesign: "website",
  branding: "brand", logos: "logo", marketing: "marketing",
  seo: "seo", ai: "ai", automation: "ai",
  areas: "area", hrs: "hours", yyc: "calgary",
};

export function normalize(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[^a-z0-9'\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function tokenize(text) {
  const out = [];
  for (const raw of normalize(text).split(" ")) {
    const cleaned = raw.replace(/^-+|-+$/g, "");
    if (!cleaned) continue;
    const word = SYNONYMS[cleaned] || cleaned;
    if (STOPWORDS.has(word)) continue;
    out.push(word);
  }
  return out;
}

// Levenshtein, capped. Bails early when the lengths are too far apart.
function editDistance(a, b) {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > 2) return 99;
  let prev = [];
  const cur = [];
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    cur[0] = i;
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    prev = cur.slice();
  }
  return prev[b.length];
}

// Best match for `target` among the visitor's tokens, plus where it matched —
// the position lets the caller prefer the head noun ("framing for an addition").
function bestTokenMatch(queryTokens, target) {
  let best = 0;
  let index = -1;
  for (let i = 0; i < queryTokens.length; i++) {
    const q = queryTokens[i];
    let s = 0;
    if (q === target) {
      s = 1;
    } else if (
      q.length > 3 && target.length > 3 &&
      Math.abs(q.length - target.length) <= 4 &&
      (q.startsWith(target) || target.startsWith(q))
    ) {
      s = 0.9; // plurals and inflections
    } else if (q.length > 5 && target.length > 5) {
      // Typos. The allowance must scale with length: at distance 2 a six-letter
      // word like "france" reaches "finance", which is a wrong answer, not a
      // typo. Longer words absorb two edits safely.
      const allowed = Math.max(q.length, target.length) >= 8 ? 2 : 1;
      if (editDistance(q, target) <= allowed) s = 0.75;
    }
    if (s > best) {
      best = s;
      index = i;
    }
  }
  return { score: best, index };
}

// Whole-phrase match respecting word boundaries. Without this, "yo" matches
// inside "you" and "ty" inside "velocity".
function containsPhrase(haystack, phrase) {
  let idx = haystack.indexOf(phrase);
  while (idx !== -1) {
    const before = idx === 0 ? " " : haystack[idx - 1];
    const afterAt = idx + phrase.length;
    const after = afterAt >= haystack.length ? " " : haystack[afterAt];
    if (!/[a-z0-9]/.test(before) && !/[a-z0-9]/.test(after)) return true;
    idx = haystack.indexOf(phrase, idx + 1);
  }
  return false;
}

export class ChatEngine {
  constructor(kb, opts = {}) {
    this.kb = kb;
    this.opts = opts;
    this._entries = null;
    this._buildWeights();
  }

  entries() {
    if (this._entries) return this._entries;
    const out = [];
    (this.kb.intents || []).forEach((entry) => out.push({ kind: "intent", entry }));
    (this.kb.faqs || []).forEach((entry) => out.push({ kind: "faq", entry }));
    (this.kb.services || []).forEach((entry) => out.push({ kind: "service", entry }));
    const decline = this.kb.policy?.declineBelow;
    if (decline?.enabled) out.push({ kind: "policy", entry: decline });
    this._entries = out;
    return out;
  }

  // Term weighting (IDF). "kerdi" appears in one entry and is worth a lot;
  // "service" and "home" appear all over the knowledge base and are worth
  // little. Without this, a match on "work" outranks a match on "kerdi".
  _buildWeights() {
    const df = Object.create(null);
    const entries = this.entries();
    for (const { entry } of entries) {
      const seen = new Set();
      const phrases = [...(entry.match || []), ...(entry.name ? [entry.name] : [])];
      for (const phrase of phrases) {
        for (const token of tokenize(phrase)) {
          if (!seen.has(token)) {
            seen.add(token);
            df[token] = (df[token] || 0) + 1;
          }
        }
      }
    }
    this._df = df;
    this._n = Math.max(entries.length, 2);
    this._logN = Math.log(this._n);
  }

  // 0.3 (everywhere in the KB) to 1.0 (unique, or a word the KB has never seen).
  weight(token) {
    const d = this._df[token] || 0;
    if (!d) return 1;
    const idf = Math.log(this._n / (1 + d)) / this._logN;
    return 0.3 + 0.7 * Math.max(0, Math.min(1, idf));
  }

  scorePhrase(phrase, nq, qt) {
    const normPhrase = normalize(phrase);
    if (!normPhrase) return null;
    const content = tokenize(phrase);

    // --- whole phrase, whole words ---
    if (containsPhrase(nq, normPhrase)) {
      let avgW = 0.5;
      if (content.length) {
        avgW = content.reduce((sum, t) => sum + this.weight(t), 0) / content.length;
      }
      return {
        score: Math.min(1, 0.55 + 0.3 * avgW + 0.06 * Math.min(content.length, 3)),
        index: nq.split(" ").indexOf(normPhrase.split(" ")[0]),
        covered: content,
      };
    }

    // A phrase that is only stopwords means nothing token by token.
    if (!content.length) return null;

    // --- weighted token overlap ---
    let hitWeight = 0;
    let phraseWeight = 0;
    let firstIdx = qt.length;
    const covered = [];

    for (const t of content) {
      const w = this.weight(t);
      phraseWeight += w;
      const m = bestTokenMatch(qt, t);
      if (m.score > 0) {
        hitWeight += w * m.score;
        covered.push(qt[m.index]);
        if (m.index < firstIdx) firstIdx = m.index;
      }
    }
    if (hitWeight === 0) return null;

    const queryWeight = qt.reduce((sum, t) => sum + this.weight(t), 0);
    const coverage = hitWeight / phraseWeight;        // how much of the phrase matched
    const precision = queryWeight ? hitWeight / queryWeight : 0; // how much of the question it explains

    // Coverage alone misleads: a one-word phrase is always 100% covered.
    // Require roughly one full-strength word of evidence before coverage counts
    // for full value, so a hit on "kerdi" outweighs a hit on "work".
    const strength = Math.min(1, hitWeight / 0.9);

    return {
      score: Math.min(0.95, coverage * 0.62 * strength + Math.min(precision, 1) * 0.33),
      index: firstIdx,
      covered,
    };
  }

  scoreEntry(item, nq, qt) {
    const phrases = [...(item.entry.match || []), ...(item.entry.name ? [item.entry.name] : [])];
    let best = 0;
    let bestIndex = qt.length;
    const allCovered = new Set();

    for (const phrase of phrases) {
      const r = this.scorePhrase(phrase, nq, qt);
      if (!r) continue;
      r.covered.forEach((t) => allCovered.add(t));
      if (r.score > best) {
        best = r.score;
        bestIndex = r.index;
      }
    }
    if (best === 0) return 0;

    // Explaining more of the question is better: "kerdi shower waterproofing"
    // should land on waterproofing (2 words explained), not bathrooms (1).
    const breadth = qt.length ? Math.min(allCovered.size / qt.length, 1) : 0;
    best += 0.06 * breadth;

    // English puts the topic first: "framing for an addition" is about framing.
    if (bestIndex >= 0 && qt.length > 1) {
      best += 0.03 * (1 - bestIndex / qt.length);
    }

    return Math.min(1, best);
  }

  rank(message) {
    const nq = normalize(message);
    const qt = tokenize(message);

    // One- and two-word messages are almost always small talk ("hi", "thanks").
    // Counted in raw words, not tokens, because "do you offer X" is four words
    // but only two tokens — and it is a real question, not a greeting.
    const rawWords = nq ? nq.split(" ").length : 0;
    const intentBoost = rawWords <= 2 ? 1.08 : 1.0;

    const results = [];
    for (const item of this.entries()) {
      let score = this.scoreEntry(item, nq, qt);
      if (item.kind === "intent") score *= intentBoost;
      if (item.kind === "policy") score *= 1.1;
      if (score > 0.05) results.push({ kind: item.kind, entry: item.entry, score });
    }
    results.sort((a, b) => b.score - a.score);

    return { top: results[0] || null, results, confidence: results.length ? results[0].score : 0 };
  }

  // Distinct options for the "did you mean" state.
  suggestions(ranked, limit = 3) {
    const out = [];
    const seen = new Set();
    for (const r of ranked.results) {
      if (out.length >= limit) break;
      const label = r.entry.name || r.entry.match?.[0];
      if (!label || seen.has(label)) continue;
      seen.add(label);
      out.push({ label, ref: r });
    }
    return out;
  }

  // Source material handed upstream when the LLM is on — keeps it on MRE facts.
  context(ranked, limit = 4) {
    return ranked.results.slice(0, limit).map((r) => ({
      title: r.entry.name || r.entry.id || "MRE information",
      body: r.entry.answer || "",
      url: r.entry.url || "",
    }));
  }
}
